
import { useEffect, useState } from "react";
import { NavigationSlotPortal } from "@/components/NavigationSlotContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

function sortHppRows(rows, key, direction, type = "text") {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a?.[key] ?? "";
    const bv = b?.[key] ?? "";
    if (type === "number") return (Number(av || 0) - Number(bv || 0)) * sign;
    return String(av).localeCompare(String(bv), "id", { numeric: true, sensitivity: "base" }) * sign;
  });
}

const blank = () => ({
  type: "ingredient",
  ref_id: "",
  name: "",
  qty: 1,
  unit: "",
  unit_cost: 0,
});

const money = (n) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

export default function HPPTab() {
  const [items, setItems] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc", type: "text" });
  const toggleSort = (key, type = "text") => setSortConfig(prev => ({
    key, type, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
  }));
  const [products, setProducts] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [productId, setProductId] = useState("");
  const [price, setPrice] = useState("");
  const [notes, setNotes] = useState("");
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [composition, setComposition] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [activeHppView, setActiveHppView] = useState("list");

  async function load() {
    try {
      const [h, p, b, c] = await Promise.all([
        api.get("/product-hpp"),
        api.get("/products"),
        api.get("/ingredients"),
        api.get("/categories"),
      ]);
      setItems(h.data || []);
      setProducts(p.data || []);
      setIngredients(b.data || []);
      setProductCategories(Array.isArray(c.data) ? c.data : (c.data?.items || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data HPP");
    }
  }

  useEffect(() => {
    load();
  }, []);

  function resetForm() {
    setFormOpen(false);
    setActiveHppView("list");
    setEditingId(null);
    setName("");
    setSku("");
    setProductId("");
    setPrice("");
    setNotes("");
    setComposition([]);
  }

  function chooseProduct(id) {
    setProductId(id);
    const p = products.find((x) => String(x.id || x._id) === id);
    setName(p?.name || "");
    setSku(p?.sku || "");
  }

  function addRow(type) {
    setComposition((old) => [...old, { ...blank(), type }]);
  }

  function updateRow(index, patch) {
    setComposition((old) =>
      old.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
  }

  function chooseComponent(index, id) {
    const row = composition[index];

    if (row.type === "ingredient") {
      const b = ingredients.find((x) => String(x.id || x._id) === id);
      updateRow(index, {
        ref_id: id,
        name: b?.name || "",
        unit: b?.unit || "",
        unit_cost: Number(b?.cost ?? b?.updated_cost ?? b?.base_cost ?? 0),
      });
      return;
    }

    const p = products.find((x) => String(x.id || x._id) === id);
    if (!p) {
      updateRow(index, { ref_id: "", name: "", unit: "", unit_cost: 0 });
      return;
    }

    const recipeCost = (p.recipe || []).reduce((sum, r) => {
      const b = ingredients.find(
        (x) => String(x.id || x._id) === String(r.ingredient_id)
      );
      const cost = Number(
        b?.cost ?? b?.updated_cost ?? b?.base_cost ?? 0
      );
      return sum + Number(r.qty || 0) * cost;
    }, 0);

    const savedHpp = items.find(
      (x) => String(x.product_id || "") === String(p.id || p._id)
    );

    updateRow(index, {
      ref_id: id,
      name: p.name || "",
      unit: p.unit || "porsi",
      unit_cost: Number(savedHpp ? totalHPP(savedHpp) : recipeCost),
    });
  }

  function totalHPP(record) {
    return (record.composition || []).reduce(
      (sum, r) => sum + Math.max(0, Number(r.qty || 0)) *
        Math.max(0, Number(r.unit_cost || 0)),
      0
    );
  }

  const formHPP = totalHPP({ composition });
  const salePrice = Math.max(0, Number(price || 0));
  const profit = salePrice - formHPP;
  const margin = salePrice > 0 ? (profit / salePrice) * 100 : 0;

  const filtered = items.filter((x) =>
    `${x.name || ""} ${x.sku || ""}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  async function save() {
    if (!productId || !products.some(
      (p) => String(p.id || p._id) === String(productId)
    )) {
      toast.error("Pilih produk dari master produk terlebih dahulu");
      return;
    }
    if (composition.some((r) => !r.name.trim())) {
      toast.error("Lengkapi pilihan bahan, resep, atau nama biaya tambahan");
      return;
    }
    if (composition.some((r) =>
      !Number.isFinite(Number(r.qty)) ||
      Number(r.qty) < 0 ||
      !Number.isFinite(Number(r.unit_cost)) ||
      Number(r.unit_cost) < 0
    )) {
      toast.error("Kuantitas dan harga harus berupa angka nonnegatif");
      return;
    }

    const selectedProduct = products.find(
      (p) => String(p.id || p._id) === String(productId)
    );
    const generatedSku =
      selectedProduct?.sku || sku.trim() ||
      `HPP-${Date.now().toString(36).toUpperCase()}`;

    const payload = {
      name: selectedProduct.name,
      sku: generatedSku,
      product_id: productId,
      sale_price: salePrice,
      composition: composition.map((r) => ({
        ...r,
        qty: Number(r.qty || 0),
        unit_cost: Number(r.unit_cost || 0),
        line_total: Number(r.qty || 0) * Number(r.unit_cost || 0),
      })),
      notes: notes.trim(),
    };

    setLoading(true);
    try {
      if (editingId) {
        await api.put(`/product-hpp/${editingId}`, payload);
      } else {
        await api.post("/product-hpp", payload);
      }
      await load();
      resetForm();
      setActiveHppView("list");
      toast.success("Data HPP berhasil disimpan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan HPP");
    } finally {
      setLoading(false);
    }
  }

  function editRecord(x) {
    setActiveHppView("new");
    setFormOpen(true);
    setEditingId(x.id);
    setName(x.name || "");
    setSku(x.sku || "");
    setProductId(x.product_id || "");
    setPrice(String(x.sale_price ?? ""));
    setNotes(x.notes || "");
    setComposition((x.composition || []).map((r) => ({ ...r })));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function deleteRecord(x) {
    if (!window.confirm(`Hapus data HPP "${x.name}"?`)) return;
    try {
      await api.delete(`/product-hpp/${x.id}`);
      if (editingId === x.id) resetForm();
      await load();
      toast.success("Data HPP dihapus");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus HPP");
    }
  }

  const recipeProducts = products.filter(
    (p) => Array.isArray(p.recipe) && p.recipe.length > 0
  );

  return (
    <div className="space-y-4">
      <NavigationSlotPortal layer={3}><div role="tablist" data-global-nav-layer="3" aria-label="Tampilan HPP" className="flex gap-5 border-b">
        <button
          type="button"
          role="tab"
          aria-selected={activeHppView === "list"}
          onClick={() => setActiveHppView("list")}
          className={`border-b-2 px-4 py-2 text-sm ${activeHppView === "list" ? "border-orange-500 font-semibold text-orange-700" : "border-transparent text-slate-600"}`}
        >
          Daftar
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeHppView === "new"}
          onClick={() => {
            if (activeHppView !== "new") resetForm();
            setActiveHppView("new");
            setFormOpen(true);
          }}
          className={`border-b-2 px-4 py-2 text-sm ${activeHppView === "new" ? "border-orange-500 font-semibold text-orange-700" : "border-transparent text-slate-600"}`}
        >
          Data Baru
        </button>
      </div></NavigationSlotPortal>
      {activeHppView === "new" && formOpen && <Card>
        <CardHeader>
          <CardTitle>
            {editingId ? "Edit HPP Produk" : "Tambah Produk HPP"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="block text-sm font-medium">Produk yang sudah ada</label>
          <select
            className="w-full rounded-md border p-2"
            value={productId}
            onChange={(e) => chooseProduct(e.target.value)}
          >
            <option value="">Pilih produk dari master produk</option>
            {products.map((p) => (
              <option key={p.id || p._id} value={p.id || p._id}>
                {p.name} {p.sku ? `(${p.sku})` : ""}
              </option>
            ))}
          </select>

          {productId && (
            <div className="rounded-md bg-muted/30 px-3 py-2 text-sm">
              <p><span className="text-muted-foreground">Nama produk:</span>{" "}
                <strong>{name}</strong></p>
              <p><span className="text-muted-foreground">SKU otomatis:</span>{" "}
                <strong>{sku || "Akan dibuat saat disimpan"}</strong></p>
            </div>
          )}

          <div className="rounded-md border p-3 space-y-3">
            <p className="font-semibold">Komposisi produk</p>
            {composition.map((r, i) => (
              <div key={i} className="rounded-md bg-muted/30 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">
                    {r.type === "ingredient"
                      ? "Bahan baku"
                      : r.type === "recipe"
                      ? "Resep / produk olahan"
                      : "Biaya tambahan"}
                  </p>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() =>
                      setComposition((old) => old.filter((_, j) => j !== i))
                    }
                  >
                    Hapus baris
                  </Button>
                </div>

                {r.type === "ingredient" && (
                  <select
                    className="w-full rounded-md border p-2"
                    value={r.ref_id}
                    onChange={(e) => chooseComponent(i, e.target.value)}
                  >
                    <option value="">Pilih bahan baku</option>
                    {ingredients.map((b) => (
                      <option key={b.id || b._id} value={b.id || b._id}>
                        {b.name} ({b.unit || "-"})
                      </option>
                    ))}
                  </select>
                )}

                {r.type === "recipe" && (
                  <select
                    className="w-full rounded-md border p-2"
                    value={r.ref_id}
                    onChange={(e) => chooseComponent(i, e.target.value)}
                  >
                    <option value="">Pilih resep / produk olahan</option>
                    {recipeProducts.map((p) => (
                      <option key={p.id || p._id} value={p.id || p._id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                )}

                {r.type === "additional" && (
                  <Input
                    placeholder="Nama biaya, misalnya kemasan atau stiker"
                    value={r.name}
                    onChange={(e) => updateRow(i, { name: e.target.value })}
                  />
                )}

                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  <div>
                    <label className="text-xs">Kuantitas</label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      value={r.qty}
                      onChange={(e) => updateRow(i, { qty: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs">Satuan</label>
                    <Input
                      placeholder="pcs / gram / porsi"
                      value={r.unit}
                      onChange={(e) => updateRow(i, { unit: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs">Harga per satuan (Rp)</label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      value={r.unit_cost}
                      onChange={(e) =>
                        updateRow(i, { unit_cost: e.target.value })
                      }
                    />
                  </div>
                  <div className="flex flex-col justify-end">
                    <label className="text-xs">Subtotal</label>
                    <p className="font-semibold py-2">
                      {money(Number(r.qty || 0) * Number(r.unit_cost || 0))}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => addRow("ingredient")}>
                + Bahan
              </Button>
              <Button type="button" variant="outline" onClick={() => addRow("recipe")}>
                + Resep
              </Button>
              <Button type="button" variant="outline" onClick={() => addRow("additional")}>
                + Biaya Tambahan
              </Button>
            </div>
          </div>

          <div className="rounded-md border p-3 space-y-2">
            <div className="flex justify-between gap-3">
              <span>Total HPP</span>
              <strong>{money(formHPP)}</strong>
            </div>
            <div>
              <label className="text-sm font-medium">Harga jual (isi manual)</label>
              <Input
                type="number"
                min="0"
                step="any"
                placeholder="Harga jual dalam rupiah"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </div>
            <div className="flex justify-between gap-3">
              <span>Laba kotor</span>
              <strong>{money(profit)}</strong>
            </div>
            <div className="flex justify-between gap-3">
              <span>Margin</span>
              <strong>{margin.toFixed(1)}%</strong>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Catatan / isi paket / aturan penyajian</label>
            <textarea
              className="w-full rounded-md border bg-background p-2 min-h-20"
              placeholder="Contoh: isi bundling, cara penyajian, ketentuan paket..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={save} disabled={loading}>
              {loading ? "Menyimpan..." : editingId ? "Simpan Perubahan" : "Simpan HPP"}
            </Button>
            <Button type="button" variant="outline" onClick={resetForm}>
              Batal
            </Button>
          </div>
        </CardContent>
      </Card>}

      {activeHppView === "list" && <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Daftar Produk HPP</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                Ringkasan HPP, harga jual, laba, dan margin produk.
              </p>
            </div>
            <Button onClick={() => {
              resetForm();
              setActiveHppView("new");
              setFormOpen(true);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}>
              + Tambah Produk HPP
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            placeholder="Cari nama produk atau SKU"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  {[
                    ["Produk","name","text"],["SKU","sku","text"],["Kategori","category","text"],
                    ["Jumlah komponen","composition","number"],["Total HPP","hpp","number"],
                    ["Harga jual","sale_price","number"],["Laba kotor","gross_profit","number"],
                    ["Margin","margin","number"],["Aksi",null,null]
                  ].map(([label,key,type]) => (
                    <th key={label} className="whitespace-nowrap px-3 py-3">
                      {key ? <button type="button" onClick={() => toggleSort(key,type)}>{label} ↕</button> : label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortHppRows(filtered.map(x => {
                  const hpp = totalHPP(x);
                  const sale = Number(x.sale_price || 0);
                  const grossProfit = sale - hpp;
                  const grossMargin = sale > 0 ? (grossProfit / sale) * 100 : 0;
                  return { ...x, composition_count: (x.composition || []).length, _sortHpp: hpp, _sortProfit: grossProfit, _sortMargin: grossMargin };
                }), ({
                  hpp: "_sortHpp",
                  gross_profit: "_sortProfit",
                  margin: "_sortMargin",
                  composition: "composition_count"
                }[sortConfig.key] || sortConfig.key), sortConfig.direction, sortConfig.type).map((x) => {
                  const hpp = totalHPP(x);
                  const sale = Number(x.sale_price || 0);
                  const grossProfit = sale - hpp;
                  const grossMargin = sale > 0 ? (grossProfit / sale) * 100 : 0;
                  return (
                    <tr key={x.id} className="border-t">
                      <td className="min-w-40 px-3 py-3 font-medium">
                        {x.name || "-"}
                        {x.notes && <p className="mt-1 max-w-xs whitespace-pre-wrap text-xs text-muted-foreground">{x.notes}</p>}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3">{x.sku || "-"}</td>
                      <td className="px-3 py-3">{productCategories.find(c => String(c.id || c._id) === String((products.find(p => String(p.id || p._id) === String(x.product_id)) || {}).category_id))?.name || "Belum dikategorikan"}</td>
                      <td className="px-3 py-3">{(x.composition || []).length}</td>
                      <td className="whitespace-nowrap px-3 py-3 font-medium">{money(hpp)}</td>
                      <td className="whitespace-nowrap px-3 py-3">{money(sale)}</td>
                      <td className="whitespace-nowrap px-3 py-3">{money(grossProfit)}</td>
                      <td className="whitespace-nowrap px-3 py-3">{grossMargin.toFixed(1)}%</td>
                      <td className="whitespace-nowrap px-3 py-3">
                        <div className="flex gap-2">
                          <Button type="button" size="sm" variant="outline" onClick={() => editRecord(x)}>
                            Edit
                          </Button>
                          <Button type="button" size="sm" variant="destructive" onClick={() => deleteRecord(x)}>
                            Hapus
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!filtered.length && (
                  <tr>
                    <td colSpan={9} className="p-6 text-center text-muted-foreground">
                      Belum ada data HPP yang cocok.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>}
    </div>
  );
}
